// Pegar (Ctrl/Cmd+V) y soltar (drag and drop) imágenes sobre una pantalla de modo —
// ADR-2026-09-19-pegar-y-soltar-imagenes-en-la-caja-de-prompt.
//
// Escucha en `document` mientras la pantalla está montada y `activo`: así pegar anda
// aunque la caja no tenga el foco, y soltar anda en cualquier punto de la pantalla, no
// sólo sobre la caja (apuntarle a un textarea con una foto es más difícil que a una
// pantalla). Un paste de TEXTO no se toca: sólo se interviene cuando hay imágenes.
//
// El velo (`VeloSoltarImagen`) lo dibuja la pantalla con `arrastrando`; el contador de
// enter/leave evita que parpadee al pasar sobre los hijos.

import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus } from 'lucide-react';
import { arrastraArchivos, imagenesDeDataTransfer } from './imagenesDeTransfer';

export function usePegarYSoltarImagenes(opts: {
  /** Sin esto no escucha nada (p. ej. mientras el sheet está cerrado). */
  activo: boolean;
  onImagenes: (archivos: File[]) => void;
  /**
   * Si se pasa, sólo cuenta lo que se pega o se suelta ADENTRO de este elemento (o sin
   * foco en nada: `body`). Lo pide Redes, que convive con el panel lateral del Agente: sin
   * esto, pegar una foto en la caja del chat la enganchaba también en la pantalla de atrás
   * (ADR-2026-10-04-pegar-imagenes-en-la-caja-de-redes). Sin esto, todo el documento.
   */
  dentroDe?: React.RefObject<HTMLElement | null>;
}): { arrastrando: boolean } {
  const { activo, dentroDe } = opts;
  const [arrastrando, setArrastrando] = useState(false);
  const onImagenesRef = useRef(opts.onImagenes);
  onImagenesRef.current = opts.onImagenes;

  useEffect(() => {
    if (!activo) return;
    let profundidad = 0;
    const esDeAca = (destino: EventTarget | null): boolean => {
      const raiz = dentroDe?.current;
      if (!raiz) return true;
      return destino === document.body || (destino instanceof Node && raiz.contains(destino));
    };

    const onPaste = (e: ClipboardEvent) => {
      if (!esDeAca(e.target)) return;
      const imagenes = imagenesDeDataTransfer(e.clipboardData);
      if (imagenes.length === 0) return;
      e.preventDefault();
      onImagenesRef.current(imagenes);
    };
    const onDragEnter = (e: DragEvent) => {
      if (!arrastraArchivos(e.dataTransfer) || !esDeAca(e.target)) return;
      profundidad += 1;
      setArrastrando(true);
    };
    const onDragOver = (e: DragEvent) => {
      if (!arrastraArchivos(e.dataTransfer)) return;
      // Sin esto el navegador abre la imagen en la pestaña al soltarla.
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const onDragLeave = (e: DragEvent) => {
      if (!arrastraArchivos(e.dataTransfer) || !esDeAca(e.target)) return;
      profundidad = Math.max(0, profundidad - 1);
      if (profundidad === 0) setArrastrando(false);
    };
    const onDrop = (e: DragEvent) => {
      if (!arrastraArchivos(e.dataTransfer)) return;
      profundidad = 0;
      setArrastrando(false);
      // Soltado afuera (el panel del Agente tiene su propio drop): no es de esta pantalla.
      if (!esDeAca(e.target)) return;
      e.preventDefault();
      const imagenes = imagenesDeDataTransfer(e.dataTransfer);
      if (imagenes.length > 0) onImagenesRef.current(imagenes);
    };

    document.addEventListener('paste', onPaste);
    document.addEventListener('dragenter', onDragEnter);
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('dragleave', onDragLeave);
    document.addEventListener('drop', onDrop);
    return () => {
      document.removeEventListener('paste', onPaste);
      document.removeEventListener('dragenter', onDragEnter);
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('dragleave', onDragLeave);
      document.removeEventListener('drop', onDrop);
      setArrastrando(false);
    };
  }, [activo, dentroDe]);

  return { arrastrando };
}

/** El velo que cubre la pantalla mientras se arrastra una imagen encima. Va ADENTRO del
 *  contenedor `fixed` de la pantalla (que ya tiene el marco), con `pointer-events-none`
 *  para no robarle el drop al documento. */
export const VeloSoltarImagen: React.FC<{ visible: boolean }> = ({ visible }) =>
  visible ? (
    <div className="pointer-events-none absolute inset-0 z-[70] flex items-center justify-center bg-[#1644EB14] backdrop-blur-[2px]">
      <div className="flex items-center gap-3 rounded-2xl border-2 border-dashed border-brand-primary bg-white/90 px-6 py-4 shadow-lg">
        <ImagePlus className="h-6 w-6 text-brand-primary" strokeWidth={2} />
        <span className="text-[15px] font-medium text-brand-black">
          Soltá la imagen para agregarla como referencia
        </span>
      </div>
    </div>
  ) : null;
